import * as HLF from "html-living-framework";

import { TimelineLite } from "gsap/TimelineLite";
import { TweenMax } from "gsap/TweenMaxBase";

// @ts-ignore
import { PixiPlugin } from "gsap/PixiPlugin";

import * as springroll from "springroll";
import { GoogleAnalytics } from "springroll-google-analytics-plugin";
// @ts-ignore
// import { LoggingPanel } from "springroll-logging-panel-plugin";

import * as Constants from "Data/Constants";
import { GameConfig, LocationID, LocationIDs } from "Data/GameConfig";

import * as CaptionData from "../json/close-caption.json";

import { GameState } from "Models/GameState";

import { CaptionManager } from "GameObjects/Captions/CaptionManager";
import { LunaCaptionRenderer } from "GameObjects/Captions/LunaCaptionRenderer";
import { KeyboardFocus } from "GameObjects/KeyboardFocus";

import PausePopup from "Popups/PausePopup";

import { CitySelect } from "States/CitySelect";
import { GameExterior } from "States/GameExterior";
import { GameInteriorGiveQuest } from "States/GameInteriorGiveQuest";
import { GameInteriorReturnItems } from "States/GameInteriorReturnItems";
import { Interstitial } from "States/Interstitial";
import { LandingPage } from "States/LandingPage";
// import { LevelSelect } from "States/LevelSelect";
import { SplashScreen } from "States/SplashScreen";

const dontTreeShakeMe = [PixiPlugin];

export class Application extends HLF.Application {

    static get instance(): Application {
        if (HLF.Application._instance === undefined) {
            HLF.Application._instance = new Application();
        }
        return HLF.Application.instance as Application;
    }

    public get analytics() {
        return this._springroll.getPlugin("GoogleAnalytics") as (GoogleAnalytics & springroll.ApplicationPlugin);
    }

    private _captions!: CaptionManager;
    private _springroll!: springroll.Application;
    private _paused: boolean = false;
    private _masterVolume: number = 1;
    private _loaded: boolean = false;

    public get paused() {
        return this._paused;
    }

    public get skipVO() {
        return this._captions.active === false
            && (this.audio.getCategoryVolume(HLF.AudioCategory.VO) === 0 || this.audio.masterVolume === 0);
    }

    private constructor() {
        super({
            transparent: true,
            antialias: true,
            preserveDrawingBuffer: (
                PIXI.utils.isMobile.android.device ||
                PIXI.utils.isMobile.amazon.device
            ) ? true : undefined,
        });
        PIXI.settings.FILTER_RESOLUTION = this.renderer.resolution;
    }

    public init(): void {
        this._stateManager.debug = false;
        this._loadManager.debug = false;
        this._popupManager.debug = false;
        this._saveManager.debug = false;
        this._voiceoverManager.debug = false;
        this._audioManager.debug = false;

        super.init();
        this.resizer.sizeMax = new PIXI.Point(Constants.GAME_MAX_WIDTH, Constants.GAME_MAX_HEIGHT);
        this.InitSpringroll();
        this.InitCaptions();

        this.hitAreaRenderer.interval = 2 / 1;
        // this.hitAreaRenderer.active = true;

        PubSub.publishSync(HLF.SET_KEYBOARD_ENABLED, true);

        this._webEventsManager.registerVisibilityChangedCallback((visible) => this.onPause(!visible));
    }

    public update(): void {
        super.update();
        const deltaTime: number = PIXI.ticker.shared.elapsedMS / 1000;
        this._captions.update(deltaTime);
    }
    protected onResize(pDelay: number = 0): void {
        super.onResize(pDelay);
        new TimelineLite().call(() => {
            this._captions?.onResize(this.resizer.getSize());
        }, undefined, this, pDelay);
    }

    protected createSplashScreen(): HLF.SplashScreen {
        // override
        return new SplashScreen();
    }

    protected createAssetMap(): void {
        // Set up map
        HLF.AssetMap.addAssetGroup(HLF.SplashScreen.NAME, [
            new HLF.TextureAsset("splash/splash-background", "images/static/splash/splash-background.jpg"),
            new HLF.AssetMapData("splash/splash-logo", HLF.AssetType.PNG),
            new HLF.AssetMapData("splash/splash-luna", HLF.AssetType.PNG),
            new HLF.AssetMapData(Constants.FONT_GEL_MED_35, HLF.AssetType.FONT),
            new HLF.AssetMapData(Constants.FONT_LUNA, HLF.AssetType.FONT),
        ]);
        HLF.AssetMap.addAssetGroup(Constants.STATE_LANDING_PAGE, [
            new HLF.TextureAsset("splash/splash-background", "images/static/splash/splash-background.jpg"),
            new HLF.AssetMapData("splash/splash-play", HLF.AssetType.PNG),
            new HLF.AssetMapData("splash/splash-logo", HLF.AssetType.PNG),
            new HLF.AssetMapData("splash/splash-luna", HLF.AssetType.PNG),
            new HLF.AudioAsset("LATW_1189_1_happy_pacer_jazz_New_Orleans_KK", HLF.AudioCategory.BGM),
        ]);
        HLF.AssetMap.addAssetGroup(Constants.STATE_CITY_SELECT, CitySelect.Assets);
        // HLF.AssetMap.addAssetGroup(Constants.STATE_LEVEL_SELECT, LevelSelect.Assets);

        for (const id of LocationIDs) {
            for (let n = GameConfig.locations[id].narratives.length - 1; n >= 0; n--) {
                for (let v = GameConfig.locations[id].narratives[n].variations.length - 1; v >= 0; v--) {

                    HLF.AssetMap.addAssetGroup(GameInteriorGiveQuest.GetIdForState(id, n, v), [
                        ...GameInteriorGiveQuest.Assets,
                        ...GameInteriorGiveQuest.GetAssetsForState(id, n, v),
                    ]);

                    HLF.AssetMap.addAssetGroup(GameInteriorReturnItems.GetIdForState(id, n, v), [
                        ...GameInteriorReturnItems.Assets,
                        ...GameInteriorReturnItems.GetAssetsForState(id, n, v),
                    ]);

                    HLF.AssetMap.addAssetGroup(GameExterior.GetIdForState(id, n, v), [
                        ...GameExterior.Assets,
                        ...GameExterior.GetAssetsForState(id, n, v),
                    ]);
                }
            }
        }
    }

    protected registerLoadingScreens(): void {
        this._loadManager.registerLoadScreen(
            Constants.LOAD_INTERSTITIAL, new Interstitial());
    }

    protected registerPopups(): void {
        this._popupManager.registerPopup(() => new PausePopup(), Constants.PAUSE);
        // this._popupManager.registerPopup(() => new SimplePopup(), Constants.POPUP_SIMPLE);
        // this._popupManager.registerPopup(() => new NestedPopup(), Constants.POPUP_NESTED);
        // this._popupManager.registerPopup(() => new NotificationPopup(), Constants.POPUP_NOTIFICATION);
    }

    protected registerStates(): void {
        this._stateManager.registerState(Constants.STATE_LANDING_PAGE, () => new LandingPage());
        this._stateManager.registerState(Constants.STATE_CITY_SELECT, () => new CitySelect());
        // this._stateManager.registerState(Constants.STATE_LEVEL_SELECT, () => new LevelSelect());

        for (const id of LocationIDs) {
            for (let n = GameConfig.locations[id].narratives.length - 1; n >= 0; n--) {
                for (let v = GameConfig.locations[id].narratives[n].variations.length - 1; v >= 0; v--) {
                    this._stateManager.registerState(
                        GameInteriorGiveQuest.GetIdForState(id, n, v), () => new GameInteriorGiveQuest());
                    this._stateManager.registerState(
                        GameInteriorReturnItems.GetIdForState(id, n, v), () => new GameInteriorReturnItems());
                    this._stateManager.registerState(GameExterior.GetIdForState(id, n, v), () => new GameExterior());
                }
            }
        }
    }

    public get requiredAssets(): HLF.AssetMapData[] {
        const assets = new Array<HLF.AssetMapData>();

        Constants.SPRITESHEET_UI.forEach(
            (page) => assets.push(new HLF.AssetMapData(page, HLF.AssetType.TEXTURE_ATLAS)),
        );

        // TODO: check if any fonts aren't being used
        assets.push(new HLF.AssetMapData(Constants.FONT_LUNA, HLF.AssetType.FONT));
        assets.push(new HLF.AssetMapData(Constants.FONT_GEL_LIGHT_22, HLF.AssetType.FONT));
        assets.push(new HLF.AssetMapData(Constants.FONT_GEL_MED_22, HLF.AssetType.FONT));
        assets.push(new HLF.AssetMapData(Constants.FONT_GEL_MED_35, HLF.AssetType.FONT));
        assets.push(new HLF.AssetMapData(Constants.FONT_GEL_MED_63, HLF.AssetType.FONT));

        assets.push(new HLF.AudioAsset("ui_click", HLF.AudioCategory.SFX));

        assets.push(new HLF.TextureAsset("loader-background", "images/static/loader-background.jpg"));

        return assets;
    }

    protected onLoadRequiredAssetsComplete(): void {
        super.onLoadRequiredAssetsComplete();
        this.stage.addChildAt(
            new HLF.Input.KeyboardFocusManager(KeyboardFocus),
            this.stage.getChildIndex(this._captions) - 1,
        );
        this._captions.setRenderer(new LunaCaptionRenderer(Constants.FONT_GEL_MED_63, 70));
        this._captions.onResize(this._size);
        this._loaded = true;

        // Splash view is visible and all required assets have been loaded.
        if (Constants.DEBUG_DEFAULT_STATE === undefined) {
            PubSub.publishSync(HLF.STATE_LOAD_STATE, new HLF.StateToken(
                Constants.STATE_LANDING_PAGE, HLF.SplashScreen.NAME, ...HLF.StateToken.TRANSITION_FIRST_VIEW,
            ));
        }
        else {
            const parts = Constants.DEBUG_DEFAULT_STATE.split("_");
            const state = GameState.Instance.DEBUG_ForceState(
                parts[2] as LocationID,
                parseInt(parts[3], 10),
                parseInt(parts[4], 10),
            );
            // console.log(parts);
            // console.log(state);
            if (parts[1] === "GameInteriorGiveQuest") {
                //
            }
            else if (parts[1] === "GameInteriorReturnItems") {
                for (const item of Object.values(state.items)) {
                    item.found = true;
                }
                state.allItemsFound = true;
                state.completed = false;
            }
            PubSub.publishSync(HLF.STATE_LOAD_STATE, new HLF.StateToken(
                {
                    id: Constants.DEBUG_DEFAULT_STATE,
                    data: state,
                },
                HLF.SplashScreen.NAME, ...HLF.StateToken.TRANSITION_FIRST_VIEW,
            ));
        }
    }

    // Springroll message listeners
    private onCaptionsMuted(pMuted: boolean): void {
        if (pMuted) {
            PubSub.publishSync(HLF.STOP_CAPTION, undefined);
        }
        this._captions.active = !pMuted;
    }

    private onSoundVolume(pResults: number): void {
        this._masterVolume = pResults;
        this.audio.masterVolume = this._masterVolume;
    }

    private onVOVolume(pResults: number): void {
        this.audio.setCategoryVolume(HLF.AudioCategory.VO, pResults);
    }

    private onMusicVolume(pResults: number): void {
        this.audio.setCategoryVolume(HLF.AudioCategory.BGM, pResults);
    }

    private onSFXVolume(pResults: number): void {
        this.audio.setCategoryVolume(HLF.AudioCategory.SFX, pResults);
    }

    private onPause(pIsPaused: boolean): void {
        // console.log("Paused: " + pIsPaused);
        const wasPaused = this._paused;
        this._paused = pIsPaused;

        if (pIsPaused && !wasPaused) {
            PubSub.publishSync(HLF.GAME_PAUSED, undefined);
            if (this._loaded) {
                PubSub.publishSync(HLF.SHOW_POPUP, new HLF.PopupToken(Constants.PAUSE, undefined, "static", false));
            }
            this.audio.masterVolume = 0;
            PIXI.ticker.shared.stop();
            TweenMax.pauseAll();
        }
        else if (!pIsPaused && wasPaused) {
            this.audio.masterVolume = this._masterVolume;
            TweenMax.resumeAll();
            PIXI.ticker.shared.start();
            PubSub.publishSync(HLF.HIDE_POPUP, Constants.PAUSE);
            PubSub.publishSync(HLF.GAME_UNPAUSED, undefined);
        }
    }

    private InitCaptions(): void {
        this._captions = new CaptionManager((CaptionData as any).default);
        this.stage.addChild(this._captions);
    }

    private InitSpringroll(): void {
        this._springroll = new springroll.Application({
            features: {
                captions: true,
                music: true,
                musicVolume: true,
                sfx: true,
                sfxVolume: true,
                sound: true,
                soundVolume: true,
                vo: true,
                voVolume: true,
            },
        });
        springroll.Application.uses(new GoogleAnalytics());
        // springroll.Application.uses(new LoggingPanel());
        this._springroll.state.pause.subscribe(this.onPause.bind(this));
        this._springroll.state.captionsMuted.subscribe(this.onCaptionsMuted.bind(this));
        this._springroll.state.soundVolume.subscribe(this.onSoundVolume.bind(this));
        this._springroll.state.musicVolume.subscribe(this.onMusicVolume.bind(this));
        this._springroll.state.voVolume.subscribe(this.onVOVolume.bind(this));
        this._springroll.state.sfxVolume.subscribe(this.onSFXVolume.bind(this));
    }
}
